import { useCallback, useMemo, useState } from "react";
import type { ThesisBindingType, ThesisDegreeLevel } from "@/lib/api/bindery";
import {
  MIN_COPIES,
  MIN_PAGES,
  MIN_SUPERVISOR_CHARS,
  MIN_TITLE_CHARS,
  URL_PATTERN,
} from "./newThesisConstants";

export interface ThesisFormState {
  degreeLevel: ThesisDegreeLevel;
  bindingType: ThesisBindingType;
  title: string;
  supervisor: string;
  pageCount: string;
  copies: string;
  colorPages: string;
  paperGrade: string;
  formattingNotes: string;
  fileUrl: string;
}

export type ThesisFieldErrors = Partial<Record<keyof ThesisFormState, string>>;

const INITIAL: ThesisFormState = {
  degreeLevel: "UG_FINAL",
  bindingType: "SOFT",
  title: "",
  supervisor: "",
  pageCount: "80",
  copies: "3",
  colorPages: "0",
  paperGrade: "80gsm",
  formattingNotes: "",
  fileUrl: "",
};

function validate(form: ThesisFormState): ThesisFieldErrors {
  const errors: ThesisFieldErrors = {};
  if (form.title.trim().length < MIN_TITLE_CHARS) {
    errors.title = `At least ${MIN_TITLE_CHARS} characters`;
  }
  if (form.supervisor.trim().length < MIN_SUPERVISOR_CHARS) {
    errors.supervisor = "Required";
  }
  const pages = Number(form.pageCount);
  if (!Number.isFinite(pages) || pages < MIN_PAGES) {
    errors.pageCount = "Enter a valid page count";
  }
  const copies = Number(form.copies);
  if (!Number.isFinite(copies) || copies < MIN_COPIES) {
    errors.copies = "At least 1 copy";
  }
  const url = form.fileUrl.trim();
  if (!url) {
    errors.fileUrl = "Required";
  } else if (!URL_PATTERN.test(url)) {
    errors.fileUrl = "Must be a valid http(s) link";
  }
  return errors;
}

export function useThesisForm() {
  const [form, setForm] = useState<ThesisFormState>(INITIAL);
  const [touched, setTouched] = useState<Partial<Record<keyof ThesisFormState, boolean>>>({});

  const errors = useMemo(() => validate(form), [form]);
  const isValid = Object.keys(errors).length === 0;

  const setField = <K extends keyof ThesisFormState>(k: K, v: ThesisFormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));
  const markTouched = (k: keyof ThesisFormState) => setTouched((p) => ({ ...p, [k]: true }));
  const markAllTouched = () =>
    setTouched({
      title: true,
      supervisor: true,
      pageCount: true,
      copies: true,
      fileUrl: true,
    });
  const errorFor = (k: keyof ThesisFormState) => (touched[k] ? errors[k] : undefined);
  const hydrate = useCallback((next: ThesisFormState) => {
    setForm(next);
    setTouched({});
  }, []);

  return { form, errors, isValid, setField, markTouched, markAllTouched, errorFor, hydrate };
}
